/**
 * BackgroundUploadsProvider - React component that initializes background uploads
 * for pending tracks on app startup.
 * 
 * This handles resuming uploads for:
 * - User-uploaded files that were interrupted
 * - Recordings that need to be uploaded
 * - Audio rendition extractions and uploads
 * 
 * Wrap your app with this provider (inside FetchProvider and AuthProvider).
 */

import React, { useEffect, useContext, useRef } from 'react';
import { FetchContext } from '../context/FetchContext';
import { AuthContext } from '../context/AuthContext';
import { ResumePendingUploadsAndProcessingsOnInit, HasPendingLocalAssetUploadsForUser } from './LocalAssetStore';
import { startBackgroundUploader as startLocalRecordingUploader, resumePendingOnInit as resumeLocalRecordingOnInit } from '../localRecording/LocalRecordingUploader';
import { LocalRecordingDBInstance, HasPendingLocalRecordingWork } from '../localRecording/LocalRecordingStorage';
import { requestPersistentStorageOnce } from '../utils/storagePersistence';

export const BackgroundUploadsProvider = ({ children }) => {
    const fetchContext = useContext(FetchContext);
    const authContext = useContext(AuthContext);
    const initializedRef = useRef(false);
    const localRecordingUploaderInitializedRef = useRef(false);
    const hasPendingWorkRef = useRef(false);

    // Ensure local-recording background uploader runs even outside the studio UI
    useEffect(() => {
        if (localRecordingUploaderInitializedRef.current) return;
        if (!fetchContext) return;
        localRecordingUploaderInitializedRef.current = true;

        void requestPersistentStorageOnce('app-init');

        startLocalRecordingUploader({
            onComplete: async (trackId, totalChunksCount) => {
                const rec = await LocalRecordingDBInstance.recordings.get(trackId);
                const uploadToken = rec?.uploadToken;
                if (!uploadToken) throw new Error('missing upload token for /recording/complete');
                const serverTrackId =
                    rec?.serverTrackId ??
                    (rec?.trackId && !rec.trackId.startsWith('local-') ? rec.trackId : null);
                if (!serverTrackId) throw new Error('server trackId unavailable for /recording/complete');

                await fetchContext.authAxios.post(
                    '/recording/complete',
                    { trackId: serverTrackId, totalChunksCount },
                    { headers: { Authorization: `Bearer ${uploadToken}` } }
                );
            },
        });
        void resumeLocalRecordingOnInit();
    }, [fetchContext]);

    // Warn on tab close/navigation when we have active recording or pending uploads.
    useEffect(() => {
        let cancelled = false;

        const computePending = async () => {
            try {
                const hasLocalRecording = await HasPendingLocalRecordingWork();
                const userId = authContext?.getUserId?.();
                const hasUploads = userId ? await HasPendingLocalAssetUploadsForUser(userId) : false;
                if (cancelled) return;
                hasPendingWorkRef.current = hasLocalRecording || hasUploads;
            } catch {
                // ignore
            }
        };

        // Poll because Dexie writes happen in background workers/services.
        computePending();
        const intervalId = setInterval(computePending, 5000);

        const handleBeforeUnload = (e) => {
            if (!hasPendingWorkRef.current) return undefined;
            e.preventDefault();
            // Most browsers ignore the message, but setting returnValue triggers the prompt.
            e.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [authContext]);

    useEffect(() => {
        // Only initialize once per session
        if (initializedRef.current) {
            return;
        }

        // Wait until we have auth and fetch context available
        if (!authContext || !fetchContext) {
            return;
        }

        // Wait until user is authenticated
        const userId = authContext.getUserId?.();
        if (!userId) {
            return;
        }

        /**
         * Gets a signed URL for initiating a resumable upload.
         * This is called for each pending track that needs to resume uploading.
         */
        const getInitUrlForUpload = async (trackId, kind) => {
            console.log('[BackgroundUploads] Initiating resumable upload for track:', trackId, 'kind:', kind);
            try {
                const config = {
                    headers: { Authorization: `Bearer ${authContext.getToken()}` },
                };
                const { data } = await fetchContext.authAxios.get(
                    `/initiate/${encodeURIComponent(trackId)}?kind=${encodeURIComponent(kind)}`,
                    config
                );
                return data?.url;
            } catch (error) {
                console.error('[BackgroundUploads] Failed to get init URL:', error);
                if (error?.response?.status === 401) {
                    // Token expired - don't throw, let the upload be retried later
                    return undefined;
                }
                throw error;
            }
        };

        // Start resuming pending uploads
        initializedRef.current = true;
        console.log('[BackgroundUploadsProvider] Starting background uploads for user:', userId);

        // Run in background (don't block rendering)
        (async () => {
            try {
                void requestPersistentStorageOnce('upload');
                await ResumePendingUploadsAndProcessingsOnInit(getInitUrlForUpload, userId);
                console.log('[BackgroundUploadsProvider] Background uploads completed');
            } catch (err) {
                console.error('[BackgroundUploadsProvider] Background uploads error:', err);
            }
        })();

    }, [authContext, fetchContext]);

    return children;
};

export default BackgroundUploadsProvider;
